/**
 * @description AcceptCompanyInvite — Thin redirect for Scenario 2 (existing user invite)
 *
 * Key features:
 * - Reads employeeId + companyId from URL search params
 * - Stores invite params in sessionStorage for the dashboard modal to pick up
 * - Redirects: logged in → /merchant/dashboard, not logged in → /sign-in
 * - The actual acceptance UI lives in CompanyInviteModal (rendered on dashboard)
 *
 * @dependencies
 * - AuthContext: For checking auth state
 *
 * @notes
 * - This is NOT the acceptance UI — it's just a routing bridge between the email
 *   link and the dashboard modal. The email links to /accept-company-invite?...,
 *   this component stores the params and sends the user where they need to go.
 * - sessionStorage key: 'pending_company_invite' (JSON { employeeId, companyId })
 */

import { useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import AuthLayout from './AuthLayout';
import { useAuth } from '../../contexts/AuthContext';

const PENDING_INVITE_KEY = 'pending_company_invite';

function AcceptCompanyInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();

  const employeeId = searchParams.get('employeeId');
  const companyId = searchParams.get('companyId');
  const hasValidParams = Boolean(employeeId && companyId);

  useEffect(() => {
    if (!isLoaded) return;
    if (!hasValidParams) return;

    // Store invite params for CompanyInviteModal to pick up on the dashboard
    sessionStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({ employeeId, companyId }));

    if (isSignedIn) {
      navigate('/merchant/dashboard', { replace: true });
    } else {
      navigate('/sign-in', { replace: true });
    }
  }, [isLoaded, isSignedIn, hasValidParams, employeeId, companyId, navigate]);

  // Loading while auth state initializes
  if (!isLoaded || hasValidParams) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </AuthLayout>
    );
  }

  // Invalid params — only shown if employeeId or companyId is missing
  return (
    <AuthLayout helper={null}>
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          Invalid invitation link
        </h1>
        <p className="text-gray-400 mb-8 text-sm">
          This invitation link is invalid or incomplete.
          Please check your email for the correct link.
        </p>
        <Link
          to="/dashboard"
          className="block w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
        >
          Go to dashboard
        </Link>
      </div>
    </AuthLayout>
  );
}

export default AcceptCompanyInvite;
